import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ConfigService } from "src/app/services/config.service";
import { AuthenticationService } from "src/app/services/authentication.service";
import { User } from "../users/models/user.model";
import { SelectItem } from "primeng/api";
import { InfoMessage } from "../users/models/info-message.model";

@Injectable()
export class UserService {
    senriClients: SelectItem[];

    constructor(
        private httpClient: HttpClient,
        private configService: ConfigService,
        private authService: AuthenticationService,
    ) {
        this.getUsers().subscribe((items) => {
            this.setUserList(items);
        });

        this.getSenriClients().subscribe((items) => {
            this.senriClients = items;
        });
    }

    userList: User[] = [];
    usersLoaded = false;

    getSenriClients() {
        return this.httpClient.get<SelectItem[]>(
            this.configService.apiBaseUrl.concat("User/SenriClients"),
        );
    }

    getUsers() {
        return this.httpClient.get<User[]>(
            this.configService.apiBaseUrl.concat("user"),
        );
    }

    getSingleUser(userId: string) {
        return this.httpClient.get<User>(
            this.configService.apiBaseUrl.concat("User/", userId),
        );
    }

    setUserList(users: User[]) {
        this.userList.length = 0;
        this.userList.push(...users);
        this.usersLoaded = true;
    }

    // Resolves a user's Company ids to their display names for the user-management dialogs.
    getCompanyNames(user: User): string {
        const ids = user.senriClientIds || [];
        if (ids.length === 0) {
            return "";
        }
        return ids
            .map((id) => {
                const item = (this.senriClients || []).find(
                    (c) => c.value === id.toString(),
                );
                return item !== undefined ? item.label : id.toString();
            })
            .join(", ");
    }

    createUser(userDto: any) {
        return this.httpClient.post<InfoMessage>(
            this.configService.apiBaseUrl.concat("User/Registration"),
            userDto,
        );
    }

    updateUser(userDto: any) {
        return this.httpClient.post<User>(
            this.configService.apiBaseUrl.concat("User/Update"),
            userDto,
        );
    }

    sendConfirmationLink(email: string) {
        return this.httpClient.get<InfoMessage>(
            this.configService.apiBaseUrl.concat("User/SendConfirmationLink"),
            {
                params: {
                    email: email,
                    callbackUrl: this.configService.confirmEmailCallbackUrl,
                },
            },
        );
    }

    sendForgotPasswordLink(email: string) {
        return this.httpClient.post<InfoMessage>(
            this.configService.apiBaseUrl.concat("User/ForgotPassword"),
            {
                email: email,
                callbackUrl: this.configService.resetPasswordCallbackUrl,
            },
        );
    }

    assignUserRole(email: string, role: string, senriClientIds: number[]) {
        return this.httpClient.post<User>(
            this.configService.apiBaseUrl.concat("User/AssignRole"),
            {
                email: email,
                role: role,
                senriClientIds: senriClientIds,
            },
        );
    }

    deleteUser(userId: string) {
        return this.httpClient.delete<User>(
            this.configService.apiBaseUrl.concat("User/Delete/", userId),
        );
    }

    addUserToList(user: User) {
        this.userList.unshift(user);
    }

    removeUser(userId: string) {
        const index = this.userList.findIndex((user) => {
            return user.id === userId;
        });
        if (index >= 0) {
            this.userList.splice(index, 1);
        }
    }

    replaceUserInList(userId: string, replacement: User) {
        const index = this.userList.findIndex((user) => {
            return user.id === userId;
        });
        if (index >= 0) {
            this.userList.splice(index, 1, replacement);
        }
    }
}
